import * as Yup from 'yup';
import { differenceInYears } from 'date-fns';
import {
  driverCarAssignmentStates,
  UMPDOnlyWithUMBIStates,
  combinedUMUIM,
  doNotShowCarLevelUMPDStates,
  policyLevelUMPDStates,
  policyLevelUMPDInUMSelectionStates,
  inexperiencedYearsLimit,
  canRejectPIPStates,
  umpdWithCollisionStates,
  lookupsJson
} from '@ourbranch/lookups';

import { hasNewCoApplicant } from 'customer/components/policy/details/helpers';
import { getPolicyStatus, PolicyStatus, isImportedPolicy } from 'core/helpers/policy-status';
import {
  haveAnyOfThisCarsSymbolsChanged,
  rapaCoverageSymbolSchemaFields,
  hasMakeModelStyleSymbols,
  vehicleTypeCodes,
  vehicleHorsepowerCodes,
  vehicleSymbolGroups
} from 'common/helpers/car-symbols-helpers';
import { validUSState, requiredString, addDriverNameToError } from 'common/helpers/yup-helpers';
import { isTrailerDeductibleWithinCap, isTrailerDeductibleUnchanged } from 'common/helpers/trailer-deductible-helpers';
import { getTrailerValueCap } from 'common/helpers/trailer-value-helpers';
import { isLimitACPEValid, getUMPDValue, getPLimitValue } from 'core/helpers/car-validations';
import { driversLicenseNumberSchema } from 'core/helpers/drivers-license-number-validations';
import { checkZipCodeRange, ZIP_CODE_REGEX } from 'common/helpers/address-helpers';
import {
  companionPolicyShapeFields,
  withPrimaryHomeownerPolicyCheck
} from 'common/validation-schemas/companion-policy.validation-schema';
import { appConfig, brandName } from 'brand-config';

Yup.addMethod(Yup.string, 'requiredString', requiredString);
Yup.addMethod(Yup.mixed, 'validUSState', validUSState);
Yup.addMethod(Yup.mixed, 'addDriverNameToError', addDriverNameToError);

// CFM auto collects driver relationship-to-insured and per-vehicle ownership; when a car isn't
// solely owned by the applicant a joint owner is required. Gated on the same brand feature flag the
// UI uses (no brand literals) so render and validation stay in sync. relationshipToInsured mirrors
// the UI's excluded-driver gating too: the field is disabled for excluded drivers, so requiring it
// there would be un-satisfiable.
// Read at schema-build time (a function, not a module const) so tests can flip the brand and the
// guard tracks the active brand. #35809
const collectsVehicleOwnershipDetails = () => appConfig?.featureFlags?.supportsVehicleOwnershipDetails ?? false;
const VEHICLE_OWNERSHIP_TYPE_IDS = [...lookupsJson.vehicleOwnershipType.map(({ id }) => id), null];
// CFM "Custom Equipment Coverage": a description is required when the limit (limitACPE) is > 0. #35599
const collectsCustomEquipmentCoverage = appConfig?.featureFlags?.customEquipmentCoverage ?? false;

// CFM hides the trailer Contents Value field (contents are not a CFM rating input — the rater sends
// trailer_contents 0 when absent), so it can't be required there (#35838). Read at validate-time so
// the guard tracks the active brand.
const brandHidesTrailerContents = () => appConfig?.featureFlags?.hideTrailerContents ?? false;
// CFM collects the trailer make/model (#36346); other brands never carried them, so no constraint there.
const brandRequiresTrailerMakeModel = () => appConfig?.featureFlags?.trailerRequiresMakeModel ?? false;

// A trailer already on the policy record with no make/model (imported before #36404, or entered
// before #36346) is grandfathered: an unrelated endorsement must not be blocked on data staff may
// not have. Matched by VIN against initialValues, like the value cap (#35121) and deductibles
// (#35431). A NEW trailer is not in initialValues, so it stays required; a stored trailer that HAS a
// make/model can't have it cleared.
const trailerMakeModelSchema = (field, label) =>
  brandRequiresTrailerMakeModel()
    ? Yup.string()
        .nullable()
        .test(field, `Trailer ${label} is required`, function (value) {
          if (typeof value === 'string' && value.trim().length > 0) {
            return true;
          }
          const stored = (this.options.context?.initialValues?.trailers ?? []).find(
            (t) => t?.VIN && t.VIN === this.parent?.VIN
          );
          return Boolean(stored) && !stored[field];
        })
    : Yup.string().nullable();
// CFM collects Driver Status (Principal / Occasional) on every rated driver (#36327). On a policy it is
// required for a rated driver NEW to the policy or one that already had a status; a driver already on
// the record without one (migrated before #36301) is grandfathered — matched by id against
// initialValues, like the trailer rules — so an unrelated endorsement can still save.
const brandRequiresDriverStatus = () => appConfig?.featureFlags?.autoRequiresDriverStatus ?? false;
const driverStatusSchema = () =>
  brandRequiresDriverStatus()
    ? Yup.string()
        .nullable()
        .test('driverStatus', '', function (value) {
          if (this.parent?.excludeDriver === true || (typeof value === 'string' && value.trim().length > 0)) {
            return true;
          }
          const stored = (this.options.context?.initialValues?.drivers ?? []).find(
            (d) => d?.id && d.id === this.parent?.id
          );
          if (stored && !stored.driverStatus) {
            return true;
          }
          const { firstName, lastName } = this.parent ?? {};
          return this.createError({
            message: `Driver status is required for ${firstName ?? ''} ${lastName ?? ''}`.trim()
          });
        })
    : Yup.string().nullable();
// CFM has no rule tying a trailer's comp/coll deductibles to an insured vehicle's (#36149); the
// filed-trailer cap (trailer*DeductibleFiled) still applies. Mirrors the offer schema.
const brandAllowsIndependentTrailerDeductibles = () =>
  appConfig?.featureFlags?.trailerDeductiblesIndependentOfVehicles ?? false;

export const autoValidationSchema = ({ policies, policy, session }) =>
  Yup.lazy(({ autoCoverage }) => {
    const skipUmbrellaBIPDValidation = policy?.overrides?.overridesForFeFormValidation?.skipUmbrellaBIPDValidation;

    // #35431: "descends from a migrated import." isImportedPolicy only checks `source`, which the
    // renewal creator sheds on the first renewal (base-renewal-creator-manager.ts deletes `source`
    // and stamps `wasRenewedFromImportedTerm`, which stays true down the whole lineage). Gating the
    // trailer-deductible grandfather on `source` alone would re-block these live, renewing CFM books
    // one term later — so honor both markers.
    const isFromImportedLineage = isImportedPolicy(policy) || policy?.wasRenewedFromImportedTerm === true;

    // Helper function
    // Pick the home policy whose status best aligns with the auto policy we're validating:
    // prefer one matching the auto's own status (e.g., future auto → future home), then fall
    // back to Active → InCancellation → Future. This avoids matching an unrelated home policy
    // (e.g., cancelled or inactive) when multiple exist on the account.
    const getMatchedHomePolicy = () => {
      const autoStatus = policy ? getPolicyStatus(policy) : undefined;
      const homePolicies = (policies || []).filter((p) => p.policyType === 'H');
      const findHomeByStatus = (status) => homePolicies.find((h) => getPolicyStatus(h) === status);
      return (
        findHomeByStatus(autoStatus) ||
        findHomeByStatus(PolicyStatus.Active) ||
        findHomeByStatus(PolicyStatus.InCancellation) ||
        findHomeByStatus(PolicyStatus.Future)
      );
    };

    const schema = Yup.object().shape({
      autoCoverage: Yup.object().shape({
        policyLimitBIPD: Yup.string().test(
          'policyLimitBIPD',
          'Umbrella coverage requires at least $500K per occurrence in auto BI.',
          (value) => {
            if (skipUmbrellaBIPDValidation) {
              return true;
            }
            if (value) {
              const matchedHomePolicy = getMatchedHomePolicy();
              if (matchedHomePolicy && matchedHomePolicy.policyDetails.includeUmbrella) {
                const policyLimitBIPDParts = value.split('/');
                if (policyLimitBIPDParts.length === 3) {
                  const perOccurrence = parseInt(policyLimitBIPDParts[1]);
                  return perOccurrence >= 500;
                }
                return value.includes('500 CSL') || value.includes('300 CSL');
              }
            }

            return true;
          }
        ),
        policyLimitMedicalPayments: Yup.string()
          .test('medicalPaymentsOrComboFBP', 'Medical payments limit is required', function (value) {
            if (this.options.context?.state === 'PA') {
              const { policyLimitComboFBP } = this.parent;
              if (policyLimitComboFBP && policyLimitComboFBP !== 'NONE' && value !== 'NONE') {
                return this.createError({
                  message: 'Can not choose both this and combined first party benefits'
                });
              }
              if (policyLimitComboFBP && policyLimitComboFBP === 'NONE' && value === 'NONE') {
                return this.createError({
                  message: 'Choose medical payments or combined first party benefits'
                });
              }
            }
            return true;
          })
          .test('medicalPaymentsOrPIPNotBothTX', 'Cannot choose both medical payments and PIP in TX', function (value) {
            if (this.options.context?.state === 'TX') {
              const { policyLimitPIP } = this.parent;
              if (value !== 'NONE' && policyLimitPIP && policyLimitPIP !== 'NONE') {
                return false;
              }
              return true;
            }
            return true;
          })
          .nullable(),
        policyLimitComboFBP: Yup.string()
          .test('medicalPaymentsOrComboFBP', 'Combined first party benefits limit is required', function (value) {
            if (this.options.context?.state === 'PA') {
              const { policyLimitMedicalPayments } = this.parent;
              if (policyLimitMedicalPayments && policyLimitMedicalPayments !== 'NONE' && value !== 'NONE') {
                return this.createError({
                  message: 'Can not choose both this and medical payments'
                });
              }
              if (policyLimitMedicalPayments && policyLimitMedicalPayments === 'NONE' && value === 'NONE') {
                return this.createError({
                  message: 'Choose medical payments or combined first party benefits'
                });
              }
            }
            return true;
          })
          .nullable(),
        policyLimitPIP: Yup.string()
          .test('medicalPaymentsOrPIPNotBothTX', 'Cannot choose both PIP and medical payments in TX', function (value) {
            if (this.options.context?.state === 'TX') {
              const { policyLimitMedicalPayments } = this.parent;
              if (value !== 'NONE' && policyLimitMedicalPayments && policyLimitMedicalPayments !== 'NONE') {
                return false;
              }
              return true;
            }
            return true;
          })
          .test(
            'policyLimitNoFaultPIPIncluded',
            'You must reject PIP coverage in order to opt out of PIP',
            function (value) {
              if (canRejectPIPStates.includes(this.options.context?.state)) {
                const { policyLimitNoFaultPIP } = this.parent;
                if (value === 'NONE' && policyLimitNoFaultPIP && policyLimitNoFaultPIP === 'INCLUDED') {
                  return false;
                }
                return true;
              }
              return true;
            }
          )
          .test(
            'policyLimitNoFaultPIPNone',
            'PIP coverage must be set to "No Coverage" if PIP is rejected',
            function (value) {
              if (canRejectPIPStates.includes(this.options.context?.state)) {
                const { policyLimitNoFaultPIP } = this.parent;
                if (value !== 'NONE' && policyLimitNoFaultPIP && policyLimitNoFaultPIP === 'NONE') {
                  return false;
                }
                return true;
              }
              return true;
            }
          )
          .test(
            'numberOfHouseholdMembersMustbeGreaterThan2ToHaveAllApplicants',
            'PIP Deductible must be applied to “Applicant Only” if there is only 1 household resident',
            function (value) {
              if (this.options.context.state === 'FL') {
                const { householdMembers } = this.options.context.auto;

                if (!value.includes('NI') && householdMembers === 1) {
                  return false;
                }
                return true;
              }
              return true;
            }
          )
          .nullable(),
        policyLimitUMBI: Yup.string()
          .test('umbiCSL', 'UM/UIM BI cannot be CSL unless policy BI is also CSL.', function () {
            const { policyLimitUMBI, policyLimitBIPD } = this.options.context?.autoCoverage || {};
            const hasAutoPolicy = this.options.context?.auto;
            if (!hasAutoPolicy) {
              return true;
            }
            const umbiCSL = policyLimitUMBI?.includes('CSL');
            const bipdCSL = policyLimitBIPD?.includes('CSL');

            return policyLimitUMBI === '0/0' || umbiCSL === bipdCSL;
          })
          .test('FL_UM/UIMBI_must_match_BIPD', 'UM/UIMBI must match BIPD limits', function (val) {
            const { policyLimitBIPD } = this.options.context?.autoCoverage || {};
            if (this.options.context.state !== 'FL' || val === '0/0') {
              return true;
            } // FL UMBI is in this format 10/20 N, but BIPD is in this format 10/20/10
            return policyLimitBIPD.includes(val.split(' ')[0]);
          }),
        policyLimitUIMBI: Yup.string().test(
          'uimbiCSL',
          'UIM BI cannot be CSL unless policy UM BI and BI/PD are also CSL.',
          function () {
            const { policyLimitBIPD, policyLimitUIMBI } = this.options.context?.autoCoverage || {};
            const hasAutoPolicy = this.options.context?.auto;
            const combinedUMUIMState = combinedUMUIM[this.options.context?.state];
            if (combinedUMUIMState || !hasAutoPolicy) {
              return true;
            }
            const bipdCSL = policyLimitBIPD.includes('CSL');
            const uimbiCSL = policyLimitUIMBI.includes('CSL');
            return policyLimitUIMBI === '0/0' || bipdCSL === uimbiCSL;
          }
        ),
        policyLimitUMPD: Yup.mixed()
          .nullable()
          .test(
            'limitUMPDMustBeLowerThanPLimitValue',
            "Error, this can't be higher than property damage limit",
            function (value) {
              if (autoCoverage.skipPDValidation) {
                return true;
              }
              const state = this.options.context?.state;
              if (state === 'NM' || !policyLevelUMPDStates[state]) {
                // in NM, we just have a UMPD = PD limit option and shouldn't run this check.
                // or if we don't show UMPD on policy level, then don't run this check.
                return true;
              }
              if (policyLevelUMPDInUMSelectionStates[state]) {
                // in these states, UMPD is the third value in policyLimitUMBI (e.g. "25/50/25")
                const umbiParts = autoCoverage.policyLimitUMBI?.split('/');
                if (!umbiParts || umbiParts.length < 3) {
                  return true;
                }
                const umpdFromUMBI = Number(umbiParts[2]);
                if (Number.isNaN(umpdFromUMBI)) {
                  return true; // can't validate, skip
                }
                return umpdFromUMBI <= getPLimitValue(autoCoverage.policyLimitBIPD);
              }
              return getUMPDValue(value) <= getPLimitValue(autoCoverage.policyLimitBIPD);
            }
          )
          .test('noLimitUMPDWithoutUMBI', 'Error, UMPD cannot be chosen in this state without UMBI', function (value) {
            const state = this.options.context?.state;
            return !(
              value !== 'NONE' &&
              UMPDOnlyWithUMBIStates[state] &&
              policyLevelUMPDStates[state] &&
              (autoCoverage.policyLimitUMBI?.startsWith('0/0') || autoCoverage.policyLimitUMBI === 'NONE')
            );
          })
      }),
      cars: Yup.array(
        Yup.object().shape({
          // Joint Ownership / PA 1200 (#35809) — mirrors the offer schema: owner identified by
          // EITHER the legacy driver pick or the free-text name; household/related answers required
          // on the name path; relationship required when related.
          // #36127: "Is the vehicle solely owned by, and registered to, the applicant?" is a Yes/No
          // question a new vehicle starts blank on, so it must be answered; only a No asks for the joint
          // owner below (a blank answer is unanswered, not "not solely owned").
          solelyOwnedByApplicant: collectsVehicleOwnershipDetails()
            ? Yup.boolean()
                .nullable()
                .required('Answer whether the vehicle is solely owned by, and registered to, the applicant')
            : Yup.boolean().nullable(),
          // #36265: required on new business only (the offer schema); a bound or migrated car nobody
          // was asked about must not block an unrelated endorsement. A stored value is list-checked.
          ownership: Yup.string()
            .nullable()
            .oneOf(VEHICLE_OWNERSHIP_TYPE_IDS, 'Vehicle ownership must be Owned, Financed or Leased'),
          jointOwner: Yup.string().nullable(),
          jointOwnerName: collectsVehicleOwnershipDetails()
            ? Yup.string()
                .when(['solelyOwnedByApplicant', 'jointOwner'], {
                  is: (solelyOwnedByApplicant, jointOwner) => solelyOwnedByApplicant === false && !jointOwner,
                  then: Yup.string()
                    .ensure()
                    .required('Joint owner name is required when the vehicle is not solely owned by the applicant')
                })
                .nullable()
            : Yup.string().nullable(),
          jointOwnerSameHousehold: collectsVehicleOwnershipDetails()
            ? Yup.boolean()
                .when(['solelyOwnedByApplicant', 'jointOwnerName'], {
                  is: (solelyOwnedByApplicant, jointOwnerName) => solelyOwnedByApplicant === false && !!jointOwnerName,
                  then: Yup.boolean()
                    .typeError('Answer whether the joint owner lives in the same household')
                    .required('Answer whether the joint owner lives in the same household')
                })
                .nullable()
            : Yup.boolean().nullable(),
          jointOwnerRelated: collectsVehicleOwnershipDetails()
            ? Yup.boolean()
                .when(['solelyOwnedByApplicant', 'jointOwnerName'], {
                  is: (solelyOwnedByApplicant, jointOwnerName) => solelyOwnedByApplicant === false && !!jointOwnerName,
                  then: Yup.boolean()
                    .typeError('Answer whether the joint owner is related by blood, marriage or adoption')
                    .required('Answer whether the joint owner is related by blood, marriage or adoption')
                })
                .nullable()
            : Yup.boolean().nullable(),
          jointOwnerRelationship: collectsVehicleOwnershipDetails()
            ? Yup.string()
                .when('jointOwnerRelated', {
                  is: true,
                  then: Yup.string().ensure().required('Relationship is required for a related joint owner')
                })
                .nullable()
            : Yup.string().nullable(),
          // AddressDetailsInput declares address: String!, so a half-filled joint-owner address
          // (city/state/zip without a street) makes AppSync reject the WHOLE save with an opaque
          // required-field error. Catch it here instead (devlo, PR #35863). Inert when untouched.
          jointOwnerAddress: Yup.object()
            .nullable()
            .default(undefined)
            .test(
              'joint-owner-street-required',
              'Joint owner street address is required when any address field is entered',
              (value) => !value || !!value.address || !(value.address2 || value.city || value.state || value.zip)
            ),
          limitUMPD: Yup.mixed()
            .nullable()
            .test(
              'limitUMPDMustBeLowerThanPLimitValue',
              "Error, this can't be higher than property damage limit",
              function (value) {
                if (autoCoverage.skipPDValidation) {
                  return true;
                }
                const state = this.options.context?.state;
                if (state === 'NM' || doNotShowCarLevelUMPDStates[state]) {
                  // in NM, we just have a UMPD = PD limit option and shouldn't run this check.
                  // or we don't have UMPD in this state or car level UMPD in this state so don't run this check
                  return true;
                }
                return getUMPDValue(value) <= getPLimitValue(autoCoverage.policyLimitBIPD);
              }
            )
            .test(
              'noLimitUMPDWithoutUMBI',
              'Error, UMPD cannot be chosen in this state without UMBI',
              function (value) {
                const state = this.options.context?.state;
                if (doNotShowCarLevelUMPDStates[state]) {
                  // we don't have UMPD in this state or car level UMPD in this state so don't run this check
                  return true;
                }
                return !(
                  value !== 'NONE' &&
                  UMPDOnlyWithUMBIStates[state] &&
                  (autoCoverage.policyLimitUMBI?.startsWith('0/0') || autoCoverage.policyLimitUMBI === 'NONE')
                );
              }
            )
            .test('Invalid', 'You may not choose UMPD Coverage when you have Collision coverage', function (value) {
              if (
                umpdWithCollisionStates.includes(this.options.context.state) ||
                doNotShowCarLevelUMPDStates[this.options.context.state]
              ) {
                return true;
              }
              if (value && value !== 'NONE') {
                const { deductibleCollision } = this.parent;
                return deductibleCollision === 'NONE';
              }
              return true;
            }),
          // #36446: street and city are required (as for trailers) — clearing the address autocomplete
          // leaves only { state, zip }, which AppSync rejects (AddressDetailsInput.address: String!).
          // City is nullable at AppSync, so a car already on the record without one (older, imported or
          // migrated) is grandfathered by VIN against initialValues, like the trailer make/model rule.
          garageLocation: Yup.object()
            .nullable()
            .shape({
              address: Yup.string()
                .requiredString('Address is required')
                .test('is-po-box', 'PO Box Addresses are invalid', (value) => {
                  const pattern = new RegExp('\\b[p]*(ost)*\\.*\\s*[o|0]*(ffice)*\\.*\\s*b[o|0]x\\b', 'i');
                  return !(pattern.test(value) || pattern.test(value));
                }),
              city: Yup.string()
                .nullable()
                .test('garage-city-required', 'City is required', function (value) {
                  if (typeof value === 'string' && value.trim().length > 0) {
                    return true;
                  }
                  const currentCarVIN = this.from?.[1]?.value?.VIN;
                  const storedCar = (this.options.context?.initialValues?.cars ?? []).find(
                    (car) => car?.VIN && car.VIN === currentCarVIN
                  );
                  return Boolean(storedCar) && !storedCar.garageLocation?.city;
                }),
              state: Yup.string()
                .nullable()
                .validUSState('auto.garageLocation.state')
                .test('garage-state-policy-test', function (value) {
                  const [_, currentCar] = this.from;
                  const { isGarageAddressRuleExceptionApproved } = currentCar.value;
                  if (isGarageAddressRuleExceptionApproved) {
                    return true;
                  }

                  if (value !== this.options?.context.state) {
                    const isInternalOrExternalAgent = session.isInternalSales || session.isExternalSales;
                    return this.createError({
                      message: `Garage address must be in the same state as the policy.${
                        isInternalOrExternalAgent
                          ? ' If an exception needs to be made, please follow your appropriate process for escalating to a Branch underwriting representative.'
                          : ''
                      }`
                    });
                  }
                  return true;
                }),
              zip: Yup.string()
                .nullable()
                .min(5, 'Zip code must be 5 digits')
                .test('garage-zip-policy-test', function (value) {
                  const [_, currentCar] = this.from;
                  const { isGarageAddressRuleExceptionApproved } = currentCar.value;
                  if (isGarageAddressRuleExceptionApproved) {
                    return true;
                  }

                  const policyState = this.options.context?.state;
                  const isValidZipCode = checkZipCodeRange(value, policyState);
                  if (!isValidZipCode) {
                    const isInternalOrExternalAgent = session.isInternalSales || session.isExternalSales;
                    return this.createError({
                      message: `Garage address must be in the valid zip code range of the policy state.${
                        isInternalOrExternalAgent
                          ? ' If an exception needs to be made, please follow your appropriate process for escalating to a Branch underwriting representative.'
                          : ''
                      }`
                    });
                  }
                  return true;
                })
            }),
          assignedDriver: Yup.mixed().test(
            'assignedDriver',
            'A driver may only be assigned  to one car',
            function (value) {
              if (driverCarAssignmentStates[this.options.context.state] && this.options.context.state !== 'MA') {
                if (!value) {
                  return this.createError({
                    message: 'All cars must be assigned a driver'
                  });
                }
                const cars = this.options.context.cars;
                const drivers = this.options.context.drivers.filter((driver) => !driver.excludeDriver);
                const assignedDrivers = cars.map((car) => car.assignedDriver);
                const driversSet = new Set(assignedDrivers);

                if (drivers.length <= cars.length) {
                  return drivers.length === driversSet.size
                    ? true
                    : this.createError({
                        message: 'Every driver must be assigned to a car'
                      });
                }
                if (drivers.length > cars.length) {
                  return cars.length <= driversSet.size
                    ? true
                    : this.createError({
                        message: 'A driver may only be assigned to one car'
                      });
                }

                return true;
              }
              return true;
            }
          ),
          deductibleCollision: Yup.string()
            .requiredString('Collision deductible is required')
            .test('Invalid', 'To add Collision, Comprehensive coverage is required.', function (value) {
              if (this.options.context.state === 'DC') {
                if (value && value !== 'NONE') {
                  const { deductibleComprehensive } = this.parent;
                  return deductibleComprehensive !== 'NONE';
                }
              }
              return true;
            })
            .test('Invalid', 'You may not have Collision coverage when you choose UMPD Coverage', function (value) {
              if (
                umpdWithCollisionStates.includes(this.options.context.state) ||
                doNotShowCarLevelUMPDStates[this.options.context.state]
              ) {
                return true;
              }
              if (value && value !== 'NONE') {
                const { limitUMPD } = this.parent;
                return !limitUMPD || limitUMPD === 'NONE';
              }
              return true;
            }),
          deductibleComprehensive: Yup.string().requiredString('Comprehensive deductible is required'),
          limitRental: Yup.string().requiredString('Rental car expense is required'),
          limitACPE: Yup.string()
            .requiredString('Additional custom parts fields is required')
            .test('Invalid', function (value) {
              if (!isLimitACPEValid(value, this.parent)) {
                return this.createError({
                  message: `To add ${
                    this.options.context?.state === 'VA' ? 'Add On Equipment' : 'Additional Custom Parts'
                  } coverage, both Collision and Comprehensive coverages are required.`
                });
              }
              return true;
            }),
          primaryUse: Yup.string().requiredString('Please select the primary use of the vehicle').nullable(),
          customEquipmentDescription: Yup.string()
            .nullable()
            .test(
              'custom-equipment-description-required',
              'Please describe the custom equipment',
              (value, { parent }) => {
                if (!collectsCustomEquipmentCoverage) {
                  return true;
                }
                const acpe = Number(parent.limitACPE);
                if (!Number.isFinite(acpe) || acpe <= 0) {
                  return true;
                }
                return !!value && value.trim().length > 0;
              }
            ),
          // Per-coverage RAPA symbols a UW enters for a farm-carrier auto: two letters each. #36504
          ...rapaCoverageSymbolSchemaFields(),
          symbolMake: Yup.string()
            .test(
              'symbolMake-length-is-2',
              'Make symbol must be exactly 2 characters',
              (value, { options: { context, parent } }) => {
                if (!context.canAddCarsManually || context.state === 'FL') {
                  return true;
                }
                if (parent.symbolAux && !hasMakeModelStyleSymbols(parent)) {
                  return value && value?.length === 2;
                }
                return haveAnyOfThisCarsSymbolsChanged(parent, context.initialValues.cars)
                  ? value && value.trim().length === 2
                  : true;
              }
            )
            .nullable(),
          symbolAux: Yup.string()
            .test(
              'symbolAux-length-is-2',
              'Auxiliary symbol must be exactly 2 characters',
              (value, { options: { context, parent } }) => {
                if (!context.canAddCarsManually || context.state === 'FL') {
                  return true;
                }
                if (hasMakeModelStyleSymbols(parent)) {
                  return value && value?.length === 2;
                }
                return haveAnyOfThisCarsSymbolsChanged(parent, context.initialValues.cars)
                  ? value && value.trim().length === 2
                  : true;
              }
            )
            .nullable(),
          symbolModel: Yup.string()
            .test(
              'symbolModel-length-is-2',
              'Model symbol must be exactly 2 characters',
              (value, { options: { context, parent } }) => {
                if (!context.canAddCarsManually || context.state === 'FL') {
                  return true;
                }
                if (parent.symbolAux && !hasMakeModelStyleSymbols(parent)) {
                  return value && value?.length === 2;
                }
                return haveAnyOfThisCarsSymbolsChanged(parent, context.initialValues.cars)
                  ? value && value.trim().length === 2
                  : true;
              }
            )
            .nullable(),
          symbolStyle: Yup.string()
            .test(
              'symbolStyle-length-is-2',
              'Style symbol must be exactly 2 characters',
              (value, { options: { context, parent } }) => {
                if (!context.canAddCarsManually || context.state === 'FL') {
                  return true;
                }
                if (parent.symbolAux && !hasMakeModelStyleSymbols(parent)) {
                  return value && value?.length === 2;
                }
                return haveAnyOfThisCarsSymbolsChanged(parent, context.initialValues.cars)
                  ? value && value.trim().length === 2
                  : true;
              }
            )
            .nullable(),
          // Ancestry Review attributes from the versioned symbol table (#35198): optional, but only
          // the filed domains may be stored (the rater substitutes its default for anything else).
          vehicleTypeCode: Yup.string()
            .nullable()
            .test(
              'vehicleTypeCode-allowed',
              `Vehicle type code must be one of ${vehicleTypeCodes.join(', ')}`,
              (value) => !value || vehicleTypeCodes.includes(value.trim().toUpperCase())
            ),
          vehicleHorsepowerCode: Yup.string()
            .nullable()
            .test(
              'vehicleHorsepowerCode-allowed',
              `Horsepower code must be one of ${vehicleHorsepowerCodes.join(', ')}`,
              (value) => !value || vehicleHorsepowerCodes.includes(value.trim().toUpperCase())
            ),
          vehicleSymbolGroup: Yup.string()
            .nullable()
            .test(
              'vehicleSymbolGroup-allowed',
              `Symbol group must be one of ${vehicleSymbolGroups.join(', ')}`,
              (value) => !value || vehicleSymbolGroups.includes(value.trim().toUpperCase())
            ),
          convertible: Yup.boolean().nullable(),
          symbolPGS: Yup.string()
            .test(
              'symbolPGS-length-is-1',
              'PGS symbol must be at least 1 character',
              (value, { options: { context, parent } }) => {
                if (!context.canAddCarsManually || context.state !== 'FL') {
                  return true;
                }
                return haveAnyOfThisCarsSymbolsChanged(parent, context.initialValues.cars)
                  ? value?.trim().length >= 1
                  : true;
              }
            )
            .nullable(),
          symbolBI: Yup.number()
            .test('symbolBI-length-is-required', 'BI symbol is required', (value, { options: { context, parent } }) => {
              if (!context.canAddCarsManually || context.state !== 'FL') {
                return true;
              }
              return haveAnyOfThisCarsSymbolsChanged(parent, context.initialValues.cars)
                ? value?.trim().length >= 0
                : true;
            })
            .nullable(),
          symbolPD: Yup.number()
            .test('symbolPD-length-is-required', 'PD symbol is required', (value, { options: { context, parent } }) => {
              if (!context.canAddCarsManually || context.state !== 'FL') {
                return true;
              }
              return haveAnyOfThisCarsSymbolsChanged(parent, context.initialValues.cars)
                ? value?.trim().length >= 0
                : true;
            })
            .nullable(),
          symbolMed: Yup.number()
            .test(
              'symbolMed-length-is-required',
              'Med symbol is required',
              (value, { options: { context, parent } }) => {
                if (!context.canAddCarsManually || context.state !== 'FL') {
                  return true;
                }
                return haveAnyOfThisCarsSymbolsChanged(parent, context.initialValues.cars)
                  ? value?.trim().length >= 0
                  : true;
              }
            )
            .nullable(),
          symbolPIP: Yup.number()
            .test(
              'symbolStyle-length-is-required',
              'PIP symbol is required',
              (value, { options: { context, parent } }) => {
                if (!context.canAddCarsManually || context.state !== 'FL') {
                  return true;
                }
                return haveAnyOfThisCarsSymbolsChanged(parent, context.initialValues.cars)
                  ? value?.trim().length >= 0
                  : true;
              }
            )
            .nullable(),
          symbolColl: Yup.number()
            .test(
              'symbolCOLL-length-is-required',
              'Collision symbol is required',
              (value, { options: { context, parent } }) => {
                if (!context.canAddCarsManually || context.state !== 'FL') {
                  return true;
                }
                return haveAnyOfThisCarsSymbolsChanged(parent, context.initialValues.cars)
                  ? value?.trim().length >= 0
                  : true;
              }
            )
            .nullable(),
          symbolComp: Yup.number()
            .test(
              'symbolCOMP-length-is-required',
              'Comprehensive symbol is required',
              (value, { options: { context, parent } }) => {
                if (!context.canAddCarsManually || context.state !== 'FL') {
                  return true;
                }
                return haveAnyOfThisCarsSymbolsChanged(parent, context.initialValues.cars)
                  ? value?.trim().length >= 0
                  : true;
              }
            )
            .nullable(),
          symbolUM: Yup.number()
            .test(
              'symbolUMUIM-length-is-required',
              'UM/UIM symbol is required',
              (value, { options: { context, parent } }) => {
                if (!context.canAddCarsManually || context.state !== 'FL') {
                  return true;
                }
                return haveAnyOfThisCarsSymbolsChanged(parent, context.initialValues.cars)
                  ? value?.trim().length >= 0
                  : true;
              }
            )
            .nullable()
        })
      )
        .min(
          1,
          'Please add a car before updating this policy. If there are no cars to insure, please follow your designated process to cancel the auto policy.'
        )
        .nullable(),
      trailers: Yup.array(
        Yup.object().shape({
          VIN: Yup.string().requiredString('VIN is required'),
          type: Yup.string().requiredString('Trailer type is required'),
          make: trailerMakeModelSchema('make', 'make'),
          model: trailerMakeModelSchema('model', 'model'),
          primaryUse: Yup.string().requiredString("Please select the trailer's primary use").nullable(),
          year: Yup.number().typeError('Trailer year should be a number').required('Trailer year is required'),
          value: Yup.number()
            .typeError('Trailer value should be a number between $0 - $50000')
            .positive('Trailer value should be between $0 - $50000')
            .integer('Trailer value should be between $0 - $50000')
            .min(1, 'Trailer value should be greater than $0')
            // On a policy modification the cap is max($50k, the value already on the policy record):
            // the migrated CFM book has trailers valued above $50k, and a fixed $50k cap blocked staff
            // from saving any edit to those policies. New/edited trailers still cap at $50k; a
            // migrated trailer keeps its stored value as the cap (matched by VIN against initialValues),
            // so it can be kept or lowered, just not raised further. #35121
            .test('maxTrailerValue', 'Trailer value should be less than or equal to $50000', function (value) {
              if (value === undefined || value === null || Number.isNaN(Number(value))) {
                return true; // typeError / required handle the empty / non-numeric cases
              }
              const cap = getTrailerValueCap(this.options.context?.initialValues?.trailers, this.parent?.VIN);
              return (
                Number(value) <= cap ||
                this.createError({ message: `Trailer value should be less than or equal to $${cap}` })
              );
            })
            .when(
              ['deductibleCollision', 'deductibleComprehensive'],
              (deductibleCollision, deductibleComprehensive, schema) => {
                const validValues = [deductibleCollision.split('/')[0], deductibleComprehensive.split('/')[0]].filter(
                  (value) => value !== 'NONE' && value
                );
                if (!validValues.length) {
                  return schema;
                }
                const minValue = Math.min(...validValues);
                return schema.min(500 + minValue, `Trailer value should be greater than ${500 + minValue}`);
              }
            )
            .required('Trailer value is required'),
          contents: brandHidesTrailerContents()
            ? // No constraints at all for a hiding brand: the user can't see or fix the field, and a
              // stored pre-#35838 value ('' or out-of-range) must not block the save. The CFM rater
              // sends trailer_contents 0 unconditionally, so a stale stored value never rates either.
              Yup.mixed().nullable()
            : Yup.number()
                .typeError('Content value should be a number between $0 - $5000')
                .positive('Content value should be between $0 - $5000')
                .integer('Content value should be between $0 - $5000')
                .min(0, 'Content value should be greater than $0')
                .max(5000, 'Content value should be less than or equal to $5000')
                .required('Trailer content value is required'),

          deductibleCollision: Yup.string()
            .requiredString('Collision deductible is required')
            .test(
              'trailerCollDeductibleFiled',
              'This collision deductible is not available on a trailer',
              (value, { options }) =>
                isTrailerDeductibleWithinCap(value, 'deductibleCollision', options.context?.state, brandName)
            )
            .test(
              'minimumCollDeductibleMet',
              'Trailer deductibles must match deductibles of at least one insured vehicle, or be NO COVERAGE for liability ONLY',
              (currentTrailerDeductibleCollision, { options }) => {
                if (brandAllowsIndependentTrailerDeductibles()) {
                  return true;
                }
                const { context, parent } = options;
                if (currentTrailerDeductibleCollision === 'NONE' && parent.deductibleComprehensive === 'NONE') {
                  return true;
                }
                // #35431: grandfather a migrated trailer whose deductibles are unchanged from the
                // imported record — those policies were imported with deductibles that match no
                // insured vehicle, and requiring a match blocked every edit. New/edited trailers
                // still must match.
                if (isFromImportedLineage && isTrailerDeductibleUnchanged(context?.initialValues?.trailers, parent)) {
                  return true;
                }
                const { cars } = context;
                for (const car of cars) {
                  if (
                    car.deductibleCollision === currentTrailerDeductibleCollision &&
                    car.deductibleComprehensive.split('/')[0] === parent.deductibleComprehensive.split('/')[0]
                  ) {
                    return true;
                  }
                }
                return false;
              }
            ),

          deductibleComprehensive: Yup.string()
            .requiredString('Comprehensive deductible is required')
            .test(
              'trailerCompDeductibleFiled',
              'This comprehensive deductible is not available on a trailer',
              (value, { options }) =>
                isTrailerDeductibleWithinCap(value, 'deductibleComprehensive', options.context?.state, brandName)
            )
            .test(
              'minimumCompDeductibleMet',
              'Trailer deductibles must match deductibles of at least one insured vehicle, or be NO COVERAGE for liability ONLY',
              (currentTrailerDeductibleComprehensive, { options }) => {
                if (brandAllowsIndependentTrailerDeductibles()) {
                  return true;
                }
                const { context, parent } = options;
                if (currentTrailerDeductibleComprehensive === 'NONE' && parent.deductibleCollision === 'NONE') {
                  return true;
                }
                // #35431: grandfather a migrated trailer whose deductibles are unchanged from the
                // imported record (see the collision rule above).
                if (isFromImportedLineage && isTrailerDeductibleUnchanged(context?.initialValues?.trailers, parent)) {
                  return true;
                }
                const { cars } = context;
                for (const car of cars) {
                  if (
                    car.deductibleComprehensive.split('/')[0] === currentTrailerDeductibleComprehensive.split('/')[0] &&
                    car.deductibleCollision === parent.deductibleCollision
                  ) {
                    return true;
                  }
                }
                return false;
              }
            ),
          garageLocation: Yup.object().shape({
            address: Yup.string()
              .requiredString('Address is required')
              .test('is-po-box', 'PO Box Addresses are invalid', (value) => {
                const pattern = new RegExp('\\b[p]*(ost)*\\.*\\s*[o|0]*(ffice)*\\.*\\s*b[o|0]x\\b', 'i');
                return !(pattern.test(value) || pattern.test(value));
              }),
            city: Yup.string().requiredString('City is required'),
            state: Yup.string().requiredString('State is required').validUSState('policy.trailers'),
            zip: Yup.string()
              .requiredString('Zip code is required')
              .matches(ZIP_CODE_REGEX, { message: 'Zip code must be 5 digits', excludeEmptyString: true })
          })
        })
      ).nullable(),
      drivers: Yup.array(
        Yup.object().shape({
          relationshipToInsured: collectsVehicleOwnershipDetails()
            ? Yup.string().when('excludeDriver', {
                is: (excludeDriver) => excludeDriver !== true,
                then: Yup.string().ensure().addDriverNameToError('Relationship to insured'),
                otherwise: Yup.string().nullable()
              })
            : Yup.string().nullable(),
          driverStatus: driverStatusSchema(),
          excludeDriver: Yup.boolean()
            .nullable()
            .test('excludeDriver', 'Error, umbrella is active on Home policy', (value) => {
              if (value) {
                const matchedHomePolicy = getMatchedHomePolicy();
                if (matchedHomePolicy) {
                  return !matchedHomePolicy.policyDetails.includeUmbrella;
                }
              }
              return true;
            }),
          dateOfBirth: Yup.string().test('minimum-driver-age', '', function (value) {
            if (value) {
              const { index } = this.options;
              const { firstName, lastName } = this.options.context.drivers[index];
              const today = new Date();
              const birthDate = new Date(value);
              const age = differenceInYears(today, birthDate);

              if (age < 14) {
                return this.createError({
                  message: `${firstName} ${lastName} is too young to be on the policy`,
                  path: this.path
                });
              }
            }
            return true;
          }),
          driversLicenseNumber: driversLicenseNumberSchema.nullable(),
          schoolName: Yup.string()
            .when('fullTimeStudent', {
              is: (fullTimeStudent) => fullTimeStudent === true,
              then: Yup.string().ensure().addDriverNameToError('School name')
            })
            .nullable(),

          schoolLocation: Yup.object()
            .when('fullTimeStudent', {
              is: (fullTimeStudent) => fullTimeStudent === true,
              then: Yup.object().shape({
                address: Yup.string().ensure().addDriverNameToError('School address'),
                city: Yup.string().ensure().addDriverNameToError('School city'),
                state: Yup.string().ensure().addDriverNameToError('School state'),
                zip: Yup.string().ensure().addDriverNameToError('School zip code').min(5, 'Zip code must be 5 digits')
              })
            })
            .nullable(),
          driversLicenseState: Yup.string()
            .requiredString('State is required')
            .validUSState('auto.drivers.driversLicenseState'),
          assignedCar: Yup.mixed().test('assignedCar', 'Each driver must be assigned to one car', function (value) {
            if (driverCarAssignmentStates[this.options.context.state] && this.options.context.state !== 'MA') {
              if (!value) {
                return this.createError({
                  message: 'All drivers must be assigned a primary vehicle'
                });
              }
              const cars = this.options.context.cars;
              if (value && !cars.some((car) => car.VIN === value)) {
                return this.createError({
                  message: 'Driver assigned VIN that does not exist '
                });
              }
              const drivers = this.options.context.drivers.filter((driver) => !driver.excludeDriver);
              const assignedDrivers = cars.map((car) => car.assignedDriver);

              if (drivers.length === cars.length) {
                const driversSet = new Set(assignedDrivers);
                // assert that each driver assigned exactly once
                return driversSet.size === assignedDrivers.length && drivers.length === driversSet.size
                  ? true
                  : this.createError({
                      message: 'Each driver must be assigned to one car'
                    });
              }

              return true;
            }
            return true;
          }),
          userAssignedCar: Yup.mixed().test('userAssignedCar', 'This car already has a main driver', function (value) {
            if (driverCarAssignmentStates[this.options.context.state] && this.options.context.state === 'MA') {
              if (!value || value === 'NONE') {
                return true;
              }
              const cars = this.options.context.cars;
              if (value && !cars.some((car) => car.VIN === value)) {
                return this.createError({
                  message: 'Driver assigned VIN that does not exist '
                });
              }
              const inexperiencedDrivers = this.options.context.drivers.filter((driver) => {
                let age = driver.age;
                if (!age) {
                  const today = new Date();
                  const birthDate = new Date(driver.dateOfBirth);
                  age = differenceInYears(today, birthDate);
                }
                return driver.age - driver.ageFirstLicensed < inexperiencedYearsLimit[this.options.context.state];
              });

              if (inexperiencedDrivers.filter((d) => d.userAssignedCar === value).length > 1) {
                return this.createError({
                  message: 'This car already has a main driver'
                });
              }
            }
            return true;
          })
        })
      ).nullable(),
      // CFM auto Underwriting Adjustment (#35787) — mirrors the offer schema rule. An UNTOUCHED
      // stored value is grandfathered whatever it is: renewal rate caps write this same field as
      // the cap multiplier (tasks-be renewals auto-rate-cap.ts, which now floors itself at 0.50 to
      // stay inside these bounds — but terms capped before #36285 carry values as low as 0.10), so
      // unconditional bounds would lock every edit on a capped policy behind a field most users
      // can't even see (#35431 lesson; devlo on PR #35852).
      auto: Yup.object()
        .shape({
          adjustmentFactor: Yup.number()
            .typeError('Adjustment factor must be a number')
            .test('adjustment-factor-range', 'Adjustment factor must be between 0.50 and 1.50', function (value) {
              if (value === null || value === undefined) {
                return true;
              }
              if (value === this.options.context?.initialValues?.auto?.adjustmentFactor) {
                return true;
              }
              return value >= 0.5 && value <= 1.5;
            })
            .test('two-decimals', 'Adjustment factor allows at most 2 decimal places', function (value) {
              if (value === null || value === undefined) {
                return true;
              }
              if (value === this.options.context?.initialValues?.auto?.adjustmentFactor) {
                return true;
              }
              return /^\d+(\.\d{1,2})?$/.test(String(value));
            })
            .nullable(),
          adjustmentRationale: Yup.string().nullable().max(1000, 'Rationale must be 1,000 characters or fewer'),
          // #35914: Companion Policy Type/Number are required once "Primary Homeowner policy with CFM?"
          // is answered Yes. grandfather:true so an unchanged (pre-existing/imported) Yes isn't forced
          // to backfill them for an unrelated endorsement — see the shared module.
          ...companionPolicyShapeFields({ grandfather: true })
        })
        .nullable()
        .default(undefined),
      fcraDisclaimer: Yup.boolean().test(
        'true-when-adding-coapp',
        'Must agree to FCRA disclaimer when adding co-applicant',
        function (value) {
          const newCoAppAdded = hasNewCoApplicant(
            this.options.context?.initialValues?.drivers,
            this.options.context?.drivers
          );

          if (newCoAppAdded) {
            return value;
          }

          // force pass because no coapp got added
          return true;
        }
      )
    });

    // #35914: block endorsement/re-rate unless the primary-homeowner question is answered Yes.
    // grandfather:true keeps existing / Guidewire-imported CFM autos serviceable (only a user's own
    // change to the answer is enforced).
    return withPrimaryHomeownerPolicyCheck({ grandfather: true })(schema);
  });
